import React from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { COLOR_BY_KEYS, ColorByType, GET_POAP_PRIMARY_VIEWS_QUERY_KEY } from 'api/queries/getPoapPrimaryViews.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { SchedulerControls } from 'components/common/bryntumScheduler/SchedulerControlsHook';

import Button from 'components/DesignSystem/Button/Button';
import { Select } from 'components/DesignSystem/Select/Select';
import { createFilterControl } from 'components/DesignSystem/Select/components/FilterControl/FilterControl';
import { Switch } from 'components/DesignSystem/Switch';
import { CustomizedTooltip } from 'components/common';
import { UseViewsManagerProvider } from 'components/pages/ScheduleViewsPage/ViewsManager/ViewsManager.provider';
import { useScheduleViewIdUrlState } from 'components/pages/ScheduleViewsPage/hooks/useScheduleViewIdUrlState';

import classes from './ViewControlsBar.module.scss';

const SHOW_BASELINE_TOOLTIP = 'Show or hide the baseline on the timeline when one is available for this schedule.';

export const COLOR_BY_OPTIONS: { label: string; value: ColorByType }[] = [
    { label: 'Status', value: COLOR_BY_KEYS.STATUS },
    { label: 'SPI', value: COLOR_BY_KEYS.SPI },
];

interface Props {
    api: UseViewsManagerProvider['api'];
    colorBy: ColorByType;
    showBaseline: boolean;
    showLabels: boolean;
    setShowBaseline: (show: boolean) => void;
    setShowLabels: (show: boolean) => void;
    setColorBy: (colorBy: ColorByType) => void;
    update: (patch: Partial<SchedulerControls>) => void;
}

const FilterControl = createFilterControl({
    icon: <MuiIcon icon={'edit'} type={'outlined'} fontSize={'2rem'} color={'inherit'} />,
    filterName: 'Color by',
});

export const ViewControlsBar = ({
    api,
    colorBy,
    showBaseline,
    showLabels,
    setShowBaseline,
    setShowLabels,
    setColorBy,
    update,
}: Props) => {
    const [viewId] = useScheduleViewIdUrlState();
    const queryClient = useQueryClient();
    const { handleSnackBar } = useCustomSnackBar();

    const { mutateAsync: setViewSettingsMutation } = api.setViewSettings.instance;
    const onSetViewSettings = async () => {
        const payload = {
            viewId: Number(viewId),
            viewControls: {
                showBaseline,
                showlabels: showLabels,
                colorBy,
            },
        };

        try {
            await setViewSettingsMutation(payload);
            handleSnackBar('Saved successfully', 'success');
            await queryClient.invalidateQueries([GET_POAP_PRIMARY_VIEWS_QUERY_KEY]);
        } catch (error) {
            DEFAULT_ERROR({ error: error as QueryError, handleError: handleSnackBar });
        }
    };
    return (
        <div id={'schedule-view-controls'} className={classes.controlsBar}>
            <div className={classes.toggleGroup}>
                <span className={classes.toggleLabel}>Show Baseline</span>
                <CustomizedTooltip
                    tooltipContent={SHOW_BASELINE_TOOLTIP}
                    triggerElement={
                        <button type={'button'} className={classes.infoIconButton} aria-label={'Show baseline info'}>
                            <MuiIcon
                                icon={'info'}
                                type={'outlined'}
                                fontSize={'2.2rem'}
                                color={'var(--text-brand-primary)'}
                            />
                        </button>
                    }
                />
                <Switch
                    className={classes.switchOnly}
                    checked={showBaseline}
                    onChange={(_, checked) => {
                        update({
                            comparisonMode: checked ? 'baseline' : 'none',
                        });
                        setShowBaseline(checked);
                    }}
                />
            </div>
            <div className={classes.toggleGroup}>
                <Switch
                    label={'Show Labels'}
                    labelPlacement={'start'}
                    checked={showLabels}
                    onChange={(_, checked) => {
                        update({
                            isShowLabels: checked,
                        });
                        setShowLabels(checked);
                    }}
                />
            </div>
            <div className={classes.colorBy}>
                <Select
                    className={classes.colorBySelect}
                    options={COLOR_BY_OPTIONS}
                    value={COLOR_BY_OPTIONS.find((item) => item.value === colorBy)}
                    onChange={(option) => {
                        setColorBy(option.value);
                        update({
                            colorByMode: option.value,
                        });
                    }}
                    isSearchable={false}
                    variant={'filter'}
                    components={{
                        Control: FilterControl,
                    }}
                />
            </div>
            <Button onClick={() => onSetViewSettings()} variant={'tertiary'}>
                Save changes
            </Button>
        </div>
    );
};
